'use client'

import { Message } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useActiveOrg, useActiveOrgUpdates } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { OrgMember, OrgMemberRole } from '~/types'
import { plural } from '~/utils/display'

export const RemoveMemberDialogContent = ({
  member,
  closeDialog,
  onRemove,
}: {
  member: OrgMember
  closeDialog: () => void
  onRemove?: () => void
}) => {
  const { org } = useActiveOrg()
  const { removeOrgMember } = useActiveOrgUpdates()
  const { smartAccounts } = useSmartAccounts()
  const { serverPostRequest, isProcessing } = useServerRequests()

  const signerOnSmartAccounts = smartAccounts.filter((account) =>
    account.signers.some(
      (signer) => signer.user && signer.user.id === member.id,
    ),
  )
  const isSigner = signerOnSmartAccounts.length > 0

  const removeMember = async () => {
    const role = member.role === OrgMemberRole.Owner ? 'Owner' : 'Member'
    const result = await serverPostRequest({
      path: '/api/org/user/remove',
      params: {
        orgId: org?.id,
        memberId: member.id,
      },
      successMessage: `${role} removed`,
      errorMessage: 'Failed to remove member',
    })
    if (result) {
      removeOrgMember(member.id)
      closeDialog()
      onRemove?.()
    }
  }

  return (
    <SplitsDialogContent
      id="removeMember"
      title={`Remove ${member.email} from workspace?`}
      variant="destructive"
      isLoading={isProcessing}
      deleteButton={{
        text: 'Remove',
        onClick: removeMember,
        eventProperties: { email: member.email },
      }}
    >
      <div className="space-y-4">
        <p>
          They will no longer be able to access the <span>{org?.name}</span>{' '}
          workspace
        </p>
        {isSigner && (
          <Message variant="warning">
            <p>
              They are a signer on {signerOnSmartAccounts.length.toString()}{' '}
              {plural('account', signerOnSmartAccounts.length)}. You can proceed
              to remove them from the workspace, but they will remain a signer
              onchain. To remove them as a signer, you can go to the account
              settings later.
            </p>
          </Message>
        )}
      </div>
    </SplitsDialogContent>
  )
}
